import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { MultiImageUploader } from "./ImageUploader";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Lock, Repeat, MapPin, Hotel, ShoppingCart, Link2 } from "lucide-react";

const STATUSES = [
  { value: "idea", label: "Idee" },
  { value: "planned", label: "Geplant" },
  { value: "booked", label: "Gebucht" },
  { value: "experienced", label: "Erlebt" },
  { value: "repeat", label: "Wiederholen" },
  { value: "archived", label: "Archiviert" },
];

const PRIORITIES = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
];

const RHYTHMS = [
  { value: "weekly", label: "Wöchentlich" },
  { value: "monthly", label: "Monatlich" },
  { value: "quarterly", label: "Quartalsweise" },
  { value: "yearly", label: "Jährlich" },
  { value: "custom", label: "Individuell" },
];

const WISH_OWNERS = [
  { value: "both", label: "Wir beide" },
  { value: "a", label: "Wunsch von mir" },
  { value: "b", label: "Wunsch von Partner:in" },
];

export const CreateEntryDialog = ({ open, onOpenChange, listId, onCreated, initial = null }) => {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [status, setStatus] = useState(initial?.status || "idea");
  const [priority, setPriority] = useState(initial?.priority || "medium");
  const [type, setType] = useState(initial?.type || "one_time");
  const [rhythm, setRhythm] = useState(initial?.rhythm || "monthly");
  const [interval, setInterval] = useState(initial?.interval || 1);
  const [nextDate, setNextDate] = useState(initial?.next_date || "");
  const [targetDate, setTargetDate] = useState(initial?.target_date || "");
  const [wishOwner, setWishOwner] = useState(initial?.wish_owner || "both");
  const [isSecret, setIsSecret] = useState(initial?.is_secret || false);
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [mapLink, setMapLink] = useState(initial?.map_link || "");
  const [hotelLink, setHotelLink] = useState(initial?.hotel_link || "");
  const [amazonLink, setAmazonLink] = useState(initial?.amazon_link || "");
  const [externalLink, setExternalLink] = useState(initial?.external_link || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [images, setImages] = useState(initial?.images || []);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const payload = {
      list_id: listId,
      title,
      description,
      status,
      priority,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      type,
      rhythm: type === "recurring" ? rhythm : null,
      interval: type === "recurring" ? Number(interval) || 1 : null,
      next_date: type === "recurring" ? nextDate || null : null,
      target_date: type === "one_time" ? targetDate || null : null,
      wish_owner: wishOwner,
      is_secret: isSecret,
      map_link: mapLink || null,
      hotel_link: hotelLink || null,
      amazon_link: amazonLink || null,
      external_link: externalLink || null,
      notes,
      images,
    };
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        const { list_id, ...rest } = payload;
        res = await api.patch(`/entries/${initial.id}`, rest);
      } else {
        res = await api.post("/entries", payload);
      }
      toast.success(isEdit ? "Eintrag aktualisiert" : "Eintrag erstellt");
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-alabaster border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="create-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">{isEdit ? "Eintrag bearbeiten" : "Neuer Eintrag"}</DialogTitle>
          <DialogDescription className="text-text_secondary">Was wollt ihr erleben, planen oder festhalten?</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6 mt-2">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Titel</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Polarlichter in Norwegen sehen"
              className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 text-lg font-serif focus-visible:ring-0 focus-visible:border-ink"
              data-testid="entry-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Beschreibung</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Was macht es so besonders?"
              className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink resize-none"
              rows={3}
              data-testid="entry-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-2 rounded-full bg-transparent border-border" data-testid="entry-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Priorität</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-2 rounded-full bg-transparent border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs value={type} onValueChange={setType}>
            <TabsList className="rounded-full bg-bone border border-border p-1">
              <TabsTrigger value="one_time" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-alabaster" data-testid="type-one-time">Einmalig</TabsTrigger>
              <TabsTrigger value="recurring" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-alabaster" data-testid="type-recurring">
                <Repeat size={13} className="mr-1.5" /> Wiederkehrend
              </TabsTrigger>
            </TabsList>
            <TabsContent value="one_time" className="mt-4">
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Zieldatum (optional)</label>
              <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className="mt-2 rounded-full bg-transparent border-border" />
            </TabsContent>
            <TabsContent value="recurring" className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Rhythmus</label>
                <Select value={rhythm} onValueChange={setRhythm}>
                  <SelectTrigger className="mt-2 rounded-full bg-transparent border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RHYTHMS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Alle X</label>
                <Input type="number" min={1} value={interval} onChange={(e) => setInterval(e.target.value)} className="mt-2 rounded-full bg-transparent border-border" />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Nächster Termin</label>
                <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="mt-2 rounded-full bg-transparent border-border" />
              </div>
            </TabsContent>
          </Tabs>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Wunsch von</label>
              <Select value={wishOwner} onValueChange={setWishOwner}>
                <SelectTrigger className="mt-2 rounded-full bg-transparent border-border" data-testid="entry-wish-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WISH_OWNERS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Tags (komma-getrennt)</label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="reise, abenteuer, winter" className="mt-2 rounded-full bg-transparent border-border" />
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white p-4 flex items-center justify-between" data-testid="secret-toggle-container">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-bone flex items-center justify-center">
                <Lock size={15} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-sm font-medium text-ink">Überraschung</div>
                <div className="text-xs text-text_secondary">Nur du siehst die Details. Partner:in sieht nur „Eine Überraschung wartet …"</div>
              </div>
            </div>
            <Switch checked={isSecret} onCheckedChange={setIsSecret} data-testid="entry-secret-switch" />
          </div>

          <div className="space-y-3">
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Links</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
                <MapPin size={14} className="text-text_secondary" />
                <input value={mapLink} onChange={(e) => setMapLink(e.target.value)} placeholder="Google Maps Link" className="bg-transparent flex-1 text-sm focus:outline-none" />
              </div>
              <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
                <Hotel size={14} className="text-text_secondary" />
                <input value={hotelLink} onChange={(e) => setHotelLink(e.target.value)} placeholder="Hotel Link" className="bg-transparent flex-1 text-sm focus:outline-none" />
              </div>
              <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
                <ShoppingCart size={14} className="text-text_secondary" />
                <input value={amazonLink} onChange={(e) => setAmazonLink(e.target.value)} placeholder="Amazon Link" className="bg-transparent flex-1 text-sm focus:outline-none" />
              </div>
              <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
                <Link2 size={14} className="text-text_secondary" />
                <input value={externalLink} onChange={(e) => setExternalLink(e.target.value)} placeholder="Anderer Link" className="bg-transparent flex-1 text-sm focus:outline-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Bilder (optional)</label>
            <div className="mt-3">
              <MultiImageUploader value={images} onChange={setImages} testid="entry-images-uploader" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Notizen</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Persönliche Gedanken …" rows={2}
              className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink resize-none" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Abbrechen</Button>
            <Button type="submit" disabled={loading || !title.trim()} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="entry-submit-button">
              {loading ? "Speichern …" : isEdit ? "Speichern" : "Eintrag hinzufügen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEntryDialog;
